import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  AfFilterPrivileges,
  AfFilterGridData
} from 'data/afFilter/afFilter.types';
import * as actions from 'data/afFilter/afFilter.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/afFilter/afFilter.selectors';
import * as afSelectors from 'data/accessFunction/accessFunction.selectors';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import * as afActions from 'data/accessFunction/accessFunction.actions';
import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import { AppToaster } from 'shared/toaster';
import AfFilterGrid from './components/AfFilterGrid';
import { afFilterColumnDefs } from '../../AfFilter.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getAfFilterList(state),
  afs: afSelectors.getAccessFunctions(state),
  filterGroups: filterGroupSelectors.getFilterGroupList(state),
  selectedAfFilter: selectors.getSelectedAfFilter(state),
});

const mapDispatch = {
  goToAfFilterCreate: actions.goToAfFilterCreate,
  goToAfFilterView: actions.goToAfFilterView,
  selectAfFilter: actions.selectAfFilter,
  getAfFilterList: actions.getAfFilterList,
  deleteAfFilter: actions.deleteAfFilter,
  getAccessFunctionList: afActions.getAllAFs,
  getFilterGroupList: filterGroupActions.getFilterGroupList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const AfFilterList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  afs,
  filterGroups,
  selectedAfFilter,
  goToAfFilterCreate,
  goToAfFilterView,
  selectAfFilter,
  getAfFilterList,
  deleteAfFilter,
  getAccessFunctionList,
  getFilterGroupList
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(afFilterColumnDefs);
  const [gridData, setGridData] = useState<AfFilterGridData[]>([]);

  useEffect(() => {
    getAfFilterList(deliveryFunctionId);
    getAccessFunctionList(deliveryFunctionId);
    getFilterGroupList();

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getAfFilterList,
    getAccessFunctionList,
    getFilterGroupList
  ]);

  useEffect(() => {
    const afFiltersForGrid: AfFilterGridData[] = [];

    if (data.length) {
      data.map((afFilter) => {
        const af = afs.find((af) => window.atob(af.id || '') === afFilter.afId);
        const fg = filterGroups.find((filterGroup) => filterGroup.fgId === afFilter.fgId);

        const afFilterGridData: AfFilterGridData = {
          ...afFilter,
          afName: af?.name || '',
          fgName: fg?.name || '',
        }

        afFiltersForGrid.push(afFilterGridData);
      })

      setGridData(afFiltersForGrid);
    }
  }, [data, afs, filterGroups])

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getAfFilterList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToAfFilterCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectAfFilter(event.api.getSelectedRows());
  };

  const handleDeleteAfFilter = () => {
    const afFilterConfigrationId = selectedAfFilter[0]?.id;
    if (afFilterConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedAfFilter[0]?.fgId,
        AuditService.SECURITY_INFO,
        AuditActionType.SECURITY_INFO_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteAfFilter(deliveryFunctionId, afFilterConfigrationId, body);
  };

  const handleGoToView = () => goToAfFilterView(history);

  const hasPrivilegeToView = privileges.includes(AfFilterPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(AfFilterPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(AfFilterPrivileges.Delete);

  return (
    <Fragment>
      <AfFilterGrid
        data={gridData}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedAfFilter={selectedAfFilter[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteAfFilter}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(AfFilterList));
